'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'

// Figma "Shop the Look" band (nodes 6228:42330 desktop / 6236:52337 mobile): full-bleed section with
// `space/48` desktop horizontal gutters (mobile `space/16`), and `Content` vertical padding — desktop
// top `space/64` + bottom `space/100`, mobile top `space/48` + bottom `space/48` — matching the sibling
// PDP reco bands (PairWith / Similar / FBT).
export const sectionClass = css({
  display: 'flex',
  flexDirection: 'column',
  // The mobile peek carousel bleeds to BOTH screen edges, but the two sides get there differently,
  // because this padding also positions the eyebrow title and the desktop arrows:
  //   · RIGHT — simply no gutter at `base`, so the next card runs to the viewport boundary instead of
  //     stopping space-16 short of it. The trailing gutter moves onto the LAST card (see
  //     `carouselWrapperClass`) so the scroll still ends with breathing room.
  //   · START — this padding STAYS (the title needs it) and the carousel VIEWPORT is pulled back over it
  //     instead, via `carouselViewportCss`, with the gutter handed to the FIRST card. Dropping the
  //     padding here would take the title to the screen edge with it.
  // Desktop keeps the symmetric space-48 frame. Same treatment as the sibling `honest-reviews-ds` band.
  //
  // LOGICAL properties (`paddingInlineStart/End`), not the physical `paddingLeft/Right` these were: the
  // bleed that cancels them is itself logical, so mixing the two would put the bleed and the gutter it
  // claws back on OPPOSITE physical sides under `dir="rtl"`. Identical output in LTR — every configured
  // locale is LTR today — so this is alignment for whoever adds an RTL locale, not a live fix.
  paddingInlineStart: { base: 'space-16', lg: 'space-48' },
  paddingInlineEnd: { base: '0', lg: 'space-48' },
  paddingTop: { base: 'space-48', lg: 'space-64' },
  paddingBottom: { base: 'space-48', lg: 'space-100' },
  // `.Widget-Divider` — the 1px rule ABOVE the band — is NOT drawn here. It used to be, as an
  // unconditional `_before` on this section, which meant the band painted a seam wherever it landed:
  // including directly under a content band like FEATURE_CARDS, where non-PL/legacy draws none.
  //
  // The seam is a PAGE-level relationship (first divider section, or between two consecutive ones),
  // not a property of this widget, so it now comes from `pageClass` in
  // `src/pages/pdp1/product-page-pl-renderer.styles.ts` alongside every other divider section. Its
  // `DIVIDER_LINE` is the same geometry this block had, declaration for declaration, so the line is
  // pixel-identical wherever it still draws — it simply no longer draws where no seam belongs.
  //
  // `position: relative` stays: it's the containing block for any absolutely-positioned descendant of
  // the band, and costs nothing now that the page rule positions its `::before` against the widget
  // wrapper (which `pageClass` makes `relative` itself).
  position: 'relative',
})

// Mirrors ProductListSectionThreeDS — containerType + item sizing via descendant selector so cqi units
// resolve against this wrapper, not the viewport. Capped at the Figma `widget/max-w` 1504 and centred
// so the 4-up desktop row matches the design. Header→row gap is the Figma `Content` gap: `space/48` on
// desktop, `space/24` on mobile (6236:52337 — title ends y=68, Row starts y=92).
export const carouselWrapperClass = css({
  display: 'flex',
  flexDirection: 'column',
  width: 'full',
  maxWidth: '1504px',
  marginInline: 'auto',
  gap: { base: 'space-24', lg: 'space-48' },
  containerType: 'inline-size',
  '& [data-slot="carousel-content"]': {
    gap: { base: 'space-16', lg: 'space-24' },
  },
  '& [data-slot="carousel-item"]': {
    minWidth: 0,
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: {
      // Mobile card is a FIXED 218px per Figma — the `ProductTile (WEB)` instance measures 218 in BOTH
      // mobile frames (390: `6236:19480`, tiles at x=0/234; 768: `6236:52337`, x=0/234/468/702), so the
      // width does not scale with the viewport; only the number of visible cards does. At 390 that is
      // one full card + a 124px peek, matching the design's scroll affordance.
      // NOTE: this deliberately diverges from the non-PL PairWith scroll track
      // (`product-list-section-two-ds`), which still uses the legacy-derived `42.67vw` (~166px at 390,
      // i.e. ~2 cards + peek). Align that surface separately if the same Figma applies there.
      base: '218px',
      lg: 'calc((100cqi - 3 * var(--web-ui-spacing-space-24)) / 4)',
    },
  },
  // The track bleeds to both screen edges on mobile, so the EDGE CARDS carry the gutters: the first
  // card keeps the resting row lined up with the eyebrow title above it, and the last card stops the
  // scroll ending hard against the viewport. Margins, NOT padding: each item is sized by a 218px
  // `flexBasis` under `box-sizing: border-box`, so padding would shrink the card by 16px instead of
  // adding room beside it. Desktop keeps the symmetric frame, so neither margin applies there.
  //
  // The leading margin survives as the at-rest inset because Embla measures it: `SlideSizes` reads
  // `startGap` as the container→first-slide distance, folds it into `contentSize`, and `measureBounded`
  // then pins the first snap to `0` (`if (isFirst) return max`). `useFreeDragCarousel` supplies the
  // `containScroll: 'trimSnaps'` that enables that gap measurement. (Embla reads the trailing gap as the
  // last slide's computed `margin-${endEdge}`, resolving `endEdge` per direction, so the logical property
  // is the one that stays correct in both.)
  '& [data-slot="carousel-item"]:first-child': {
    marginInlineStart: { base: 'space-16', lg: '0' },
  },
  '& [data-slot="carousel-item"]:last-child': {
    marginInlineEnd: { base: 'space-16', lg: '0' },
  },
})

// Left half of the mobile full-bleed (the right half is just the missing `paddingRight` on
// `sectionClass`). Pull the carousel VIEWPORT back over the band's `paddingLeft` and add that 16px to
// its width: the viewport then spans exactly 100vw, so cards scroll off the left screen edge instead of
// being clipped at the gutter — while the title, the desktop arrows and the `_before` divider all keep
// the gutter, since only the viewport moves.
//
// The viewport is also the element that matters: it owns the `overflow: hidden`, so its edge IS the
// clip the cards are cut off at. web-ui merges this into the viewport's own `css()` call
// (`css({ overflow, minWidth, width }, viewportCss)`), so this `width` beats the slot's default
// `width: 'full'` deterministically. The two bare `LoaderWrapper` divs in between are unstyled, so the
// percentage and the negative margin both resolve against `carouselWrapperClass`'s content box.
//
// Reset at `lg`: the band takes its symmetric `space-48` back, the row is a 1504-capped 4-up grid, and
// `containerType: inline-size` lives on `carouselWrapperClass` — so widening the viewport never
// disturbs the desktop `100cqi` card formula either way.
export const carouselViewportCss = css.raw({
  width: { base: 'calc(100% + var(--web-ui-spacing-space-16))', lg: 'full' },
  marginInlineStart: { base: 'calc(var(--web-ui-spacing-space-16) * -1)', lg: '0' },
})

export const headerRowClass = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'space-16',
  width: 'full',
})

// Title — the Figma "Shop the Look" eyebrow: Grosa Medium, UPPERCASE, 0.05em tracking. The DS
// `title-16` (desktop `title/16`) and `title-14` (mobile `title/14`) textStyles already carry the
// uppercase + letter-spacing, so the source copy stays normal-case (CSS uppercases it). NOT the
// decorative ivy `-dec` serif — PDP reco swimlanes use the upright grosa eyebrow.
export const headingClass = css({
  textStyle: { base: 'title-14', lg: 'title-16' },
  color: 'black',
  textAlign: 'left',
  flex: '1 0 0',
  minWidth: 0,
})

export const navGroupClass = css({
  display: { base: 'none', lg: 'flex' },
  gap: 'space-4',
  alignItems: 'center',
  flexShrink: 0,
  marginLeft: 'auto',
})

export const loaderWrapperClass = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
})
